/**
 * The pure parts of the Rule 502(b) package screen (S-30): wording and the forms' own checks. The
 * server checks everything again; these only keep a manager from sending what it will refuse.
 */
import { formatDateTime } from '../../lib/datetime';
import type {
  CoveredPerson,
  Inquiry,
  Issuer,
  IssuerDetail,
  IssuerDocType,
  IssuerDocument,
  OfferingAmount,
} from '../../services/regDStaff.service';

export const TIER_LABEL: Record<OfferingAmount['tier'], string> = {
  part_fs_b: 'Up to USD 20,000,000: statements need not be audited',
  part_fs_c: 'Over USD 20,000,000: statements must be audited',
};

export const ISSUER_DOC_LABEL: Record<IssuerDocType, string> = {
  reg_d_package: 'Information package',
  exhibit: 'Exhibit',
  bad_actor_disclosure: 'Disclosure of past events (506(e))',
};

export const ISSUER_DOC_TYPES = Object.keys(ISSUER_DOC_LABEL) as IssuerDocType[];

/** One person per line, "Name, role" (the role may be missing: then "covered person"). */
export function parseCoveredPersons(text: string): CoveredPerson[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .map((line) => {
      const [name, ...rest] = line.split(',');
      return { name: name.trim(), role: rest.join(',').trim() || 'covered person' };
    });
}

/** Whether the amount is a positive number with at most two decimals. */
export function validAmount(text: string): boolean {
  return /^\d{1,22}(\.\d{1,2})?$/.test(text.trim()) && Number(text) > 0;
}

export const SLOT_PATTERN = /^[a-z][a-z0-9_]{2,39}$/;

/** A short name for an exhibit: "operating agreement" becomes the slot operating_agreement. */
export function slotFrom(name: string): string {
  return name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join('_')
    .slice(0, 40);
}

export interface UploadForm {
  docType: IssuerDocType;
  exhibitName: string;
  hasFile: boolean;
  statementsDate: string;
  inquiryId: string;
}

/** What is missing from an upload form, in words; empty when it can be sent. */
export function uploadProblem(form: UploadForm): string | null {
  if (!form.hasFile) return 'Choose the PDF.';
  if (form.docType === 'exhibit' && !SLOT_PATTERN.test(slotFrom(form.exhibitName))) {
    return 'Name the exhibit in a few words.';
  }
  if (form.docType === 'reg_d_package') {
    if (form.statementsDate === '') return 'Give the date of the financial statements.';
    if (form.inquiryId === '') return 'Choose the bad-actor inquiry the package rests on.';
  }
  return null;
}

/** The documents of one kind, the current (published) one first. */
export function documentsOf(detail: IssuerDetail, docType: IssuerDocType): IssuerDocument[] {
  return detail.documents
    .filter((d) => d.doc_type === docType)
    .sort((a, b) => Number(b.is_published) - Number(a.is_published) || b.version - a.version);
}

export function documentLine(doc: IssuerDocument): string {
  const base = ISSUER_DOC_LABEL[doc.doc_type];
  const slot = doc.exhibit_slot ? `: ${doc.exhibit_slot.replace(/_/g, ' ')}` : '';
  return `${base}${slot} · version ${doc.version}`;
}

/** The line of the issuer picker: the products of its series, then a short form of its id. */
export function issuerOption(issuer: Issuer): string {
  const names = issuer.products.map((p) => p.name).join(', ') || 'No product known';
  return `${names} · ${issuer.legal_entity_id.slice(0, 8)}`;
}

/** The line of the inquiry picker. */
export function inquiryOption(inquiry: Inquiry): string {
  const found = inquiry.result === 'none' ? 'nothing found' : 'past event';
  return `${formatDateTime(inquiry.performed_at)} · ${found}`;
}

/** When a document was added, published and, for a package, what its statements are. */
export function documentDetail(doc: IssuerDocument): string {
  const parts = [`added ${formatDateTime(doc.created_at)}`];
  if (doc.published_at) parts.push(`published ${formatDateTime(doc.published_at)}`);
  if (doc.fs_as_of) {
    parts.push(`statements of ${doc.fs_as_of}, ${doc.fs_audited ? 'audited' : 'unaudited'}`);
  }
  return parts.join(' · ');
}
